import React, { useState } from "react";

import "./ContactUs.css";
import { Phone, Mail, MapPin } from "lucide-react";
import { CDN_BASE_URL } from "../../config/cdn";

// ---------- Component 1: Hero Section ----------
const ContactHero = ({ image, heading, overlayColor }) => (
  <div
    className="contact-hero"
    style={{
      backgroundImage: `url(${image})`,
      backgroundColor: overlayColor,
    }}
  >
    <div className="overlay" />
    <h1>{heading}</h1>
  </div>
);

// ---------- Component 2: Intro Section ----------
const ContactIntro = ({ bgColor, textColor }) => (
  <section className="contact-section" style={{ backgroundColor: bgColor, color: textColor }}>
    <h2>How Can We Help You?</h2>
    <p>
      Whether you're exploring our services, interested in a demo, or need assistance as a client, we're ready to help.
    </p>
  </section>
);

// ---------- Combined Component: Connect Info Cards ----------
const ConnectInfoSection = ({ bgColor = "#ffffff", textColor = "#000", cardColor = "#f5f5f5" }) => {
  const cards = [
    {
      title: "Connect With Us",
      text: "Have a question or need assistance? Fill out the form for general inquiries, and we’ll get back to you soon.",
    },
    {
      title: "Find Your Perfect Solution",
      text: "Get expert consultation with no obligations, explore customized solutions tailored to your healthcare organization's needs, and leverage HFMA peer-reviewed, data-driven tools to enhance efficiency and financial performance.",
    },
    {
      title: "Simplify Your Workday Today",
      text: "We’re excited to show you how we can support your organization! Fill out your contact details, and our team will reach out soon to discuss your needs and schedule a demo.",
    },
  ];

  return (
    <section
      className="connect-info-section"
      style={{ backgroundColor: bgColor, color: textColor }}
    >
      <div className="connect-info-container">
        {cards.map((card, index) => (
          <div
            key={index}
            className="connect-card"
            style={{ backgroundColor: cardColor }}
          >
            <h3>{card.title}</h3>
            <p>{card.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
};


interface OfficeInfoProps {
  bgColor?: string;
  titleColor?: string;
  textColor?: string;
  iconBgColor?: string;
  officeTitle?: string;
  officeSubtitle?: string;
  phone?: string;
  email?: string;
  address?: string;
}

export function OfficeInfoSection({
  bgColor = "#ffffff",
  titleColor = "#f05a14ff",
  textColor = "#000000",
  iconBgColor = "#075e59",
  officeTitle = "Corporate Office",
  officeSubtitle = "Piscataway, NJ",
  phone = "(732)-335-6510",
  email = "info@medrcm360.com",
  address = "15 Corporate Place South, Suite 312, Piscataway, NJ 08854",
}: OfficeInfoProps) {
  return (
    <section style={{ backgroundColor: bgColor }} className="py-16 w-full">
      <div className="max-w-7xl mx-auto text-center">
        <h2
          className="text-4xl font-bold"
          style={{ color: titleColor }}
        >
          {officeTitle}
        </h2>



        <p
          className="text-3xl font-semibold mt-2"
          style={{ color: titleColor }}
        >
          {officeSubtitle}
        </p>



        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mt-12">
          <InfoCard
            icon={<Phone className="h-6 w-6 text-white" />}
            label="Contact"
            value={phone}
            iconBgColor={iconBgColor}
            textColor={textColor}
          />
          <InfoCard
            icon={<Mail className="h-6 w-6 text-white" />}
            label="Mail"
            value={email}
            iconBgColor={iconBgColor}
            textColor={textColor}
          />
          <InfoCard
            icon={<MapPin className="h-6 w-6 text-white" />}
            label="Location"
            value={address}
            iconBgColor={iconBgColor}
            textColor={textColor}
          />
        </div>
      </div>
    </section>
  );
}

const InfoCard = ({
  icon,
  label,
  value,
  iconBgColor,
  textColor,
}: {
  icon: any;
  label: string;
  value: string;
  iconBgColor: string;
  textColor: string;
}) => {
  return (
    <div className="flex items-start gap-4 justify-center">
      <div
        className="h-14 w-14 rounded-full flex items-center justify-center"
        style={{ backgroundColor: iconBgColor }}
      >
        {icon}
      </div>
      <div className="text-left">
        <h4 className="text-xl font-semibold" style={{ color: iconBgColor }}>
          {label}
        </h4>
        <p className="text-base" style={{ color: textColor }}>
          {value}
        </p>
      </div>
    </div>
  );
};


// ---------- Component 6: Contact Form ----------

const ContactForm = ({
  bgColor = "#ffffff",
  textColor = "#333",
}) => {
  const [successPopup, setSuccessPopup] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    hospital: "",
    facilityType: "",
    query: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch("http://localhost:5000/submit-form", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pageName: "Contact Us Page",
          ...formData,
        }),
      });

      const result = await response.json();
      if (result.success) {
        setSuccessPopup(true);
        setTimeout(() => setSuccessPopup(false), 3000);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  return (
    <>
      <section className="contact-form-section" style={{ backgroundColor: bgColor, color: textColor }}>
        <div className="contact-layout-row">
          {/* LEFT FORM */}
          <form className="contact-form" onSubmit={handleSubmit}>
            <h2 className="contact-title">Contact Us</h2>

            <label>
              Name *
              <input type="text" name="name" required placeholder="Enter your name" onChange={handleChange} />
            </label>

            <label>
              Email *
              <input type="email" name="email" required placeholder="Enter your email" onChange={handleChange} />
            </label>

            <label>
              Phone
              <input type="text" name="phone" placeholder="Enter your phone number" onChange={handleChange} />
            </label>

            <label>
              Hospital
              <input type="text" name="hospital" placeholder="Enter your hospital name" onChange={handleChange} />
            </label>

            <label>
              Healthcare Facility Type *
              <select name="facilityType" required onChange={handleChange}>
                <option value="">Select a facility type</option>
                <option>Hospital</option>
                <option>Clinic</option>
                <option>Private Practice</option>
                <option>Other</option>
              </select>
            </label>

            <label>
              Query
              <textarea name="query" placeholder="Add any relevant information below" onChange={handleChange}></textarea>
            </label>

            <button className="contact-btn" type="submit">
              Schedule my demo
            </button>
          </form>

          {/* RIGHT SIDE MAP IMAGE */}
          <div className="contact-map-card">
            <a
              href="https://www.google.com/maps/search/?api=1&query=15+Corporate+Place+South+Suite+312+Piscataway+NJ+08854"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={`${CDN_BASE_URL}/icons/image.png`}
                alt="MedRCM Office Location"
                className="contact-map-image"
              />
            </a>

            <div className="contact-map-address">
              15 Corporate Place South, Suite 312, Piscataway, NJ 08854
            </div>
          </div>
        </div>
      </section>

      {/* SUCCESS POPUP */}
      {successPopup && (
        <div className="modal-overlay">
          <div className="modal-box success-modal">
            <h3>Thank You!</h3>
            <p>Our specialist will contact you soon.</p>
          </div>
        </div>
      )}
    </>
  );
};


// ---------- Component 7: Corporate Office Info ----------
const CorporateOffice = ({
  bgColor = "linear-gradient(to right, #10A8A0, #1FEAE0)", // ✅ Gradient background
  textColor = "#ffffff",
}) => (
  <section
    className="corporate-section"
    style={{
      background: bgColor, // ✅ 'background' instead of backgroundColor to allow gradient
      color: textColor,
      padding: "80px 20px",
      textAlign: "center",
      position: "relative",
      overflow: "hidden",
    }}
  >
    <div className="corporate-content" style={{ position: "relative", zIndex: 2 }}>
      <h2
        style={{
          fontSize: "2.5rem",
          marginBottom: "0.5rem",
          color: textColor,
          fontWeight: "700",
        }}
      >
        Corporate Office
      </h2>
      <p
        style={{
          fontSize: "1.2rem",
          marginBottom: "2rem",
          fontWeight: "500",
        }}
      >
        Piscataway, NJ
      </p>

      <div
        className="contact-details"
        style={{
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: "50px",
        }}
      >
        <div
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            padding: "20px 30px",
            borderRadius: "10px",
            minWidth: "220px",
            transition: "all 0.3s ease",
          }}
          className="contact-card"
        >
          <strong style={{ fontSize: "1.2rem" }}>📞 Contact</strong>
          <p style={{ marginTop: "10px", fontSize: "1.1rem" }}>(732)-335-6510</p>
        </div>

        <div
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            padding: "20px 30px",
            borderRadius: "10px",
            minWidth: "220px",
            transition: "all 0.3s ease",
          }}
          className="contact-card"
        >
          <strong style={{ fontSize: "1.2rem" }}>✉️ Mail</strong>
          <p style={{ marginTop: "10px", fontSize: "1.1rem" }}>info@medrcm360.com</p>
        </div>

        <div
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            padding: "20px 30px",
            borderRadius: "10px",
            minWidth: "220px",
            transition: "all 0.3s ease",
          }}
          className="contact-card"
        >
          <strong style={{ fontSize: "1.2rem" }}>📍 Location</strong>
          <p style={{ marginTop: "10px", fontSize: "1.1rem", lineHeight: "1.6" }}>
            15 Corporate Place South, Suite 312, <br />
            Piscataway, NJ 08854
          </p>
        </div>
      </div>
    </div>
  </section>
);


// ---------- Main ContactUs Page ----------
const ContactUs = () => {
  return (
    <div className="contact-page">
      <ContactHero
        image={`${CDN_BASE_URL}/images/landing-top-image.png`}
        heading="Contact Us"
        overlayColor="rgba(0, 0, 0, 0.4)"
      />
      <ContactIntro bgColor="#ffffff" textColor="#000" />
      <ConnectInfoSection bgColor="#ffffff" textColor="#000" cardColor="#f5f5f5" />

      <ContactForm bgColor="#ffffff" textColor="#000" buttonColor="#006666" />
      <CorporateOffice
        bgColor="linear-gradient(to right, #10A8A0, #1FEAE0)"
        textColor="#ffffff"
      />

    </div>
  );
};

export default ContactUs;
